import { LayoutDashboard, Radar, Inbox, Workflow, CalendarDays, Sparkles, LineChart, PenTool, FileText, Quote, BookOpen, Image as ImageIcon, Cpu, TrendingUp, ListChecks, Bell, Bot, type LucideIcon } from "lucide-react";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar,
} from "@/components/ui/sidebar";

export type AdminTabId =
  | "command"
  | "visitors" | "submissions" | "pipeline"
  | "calendar" | "tasks" | "followups"
  | "seoai" | "aidiscover" | "gsc" | "seo" | "contentops" | "blog"
  | "testimonials" | "cases" | "assets"
  | "aiops";

interface NavItem { id: AdminTabId; label: string; icon: LucideIcon }
interface NavGroup { label: string; emoji: string; items: NavItem[] }

const GROUPS: NavGroup[] = [
  {
    label: "Command Center", emoji: "🏠",
    items: [{ id: "command", label: "Executive Dashboard", icon: LayoutDashboard }],
  },
  {
    label: "Lead Generation", emoji: "🎯",
    items: [
      { id: "visitors",    label: "Visitors",          icon: Radar },
      { id: "submissions", label: "Lead Intelligence", icon: Inbox },
      { id: "pipeline",    label: "Pipeline & Revenue", icon: Workflow },
    ],
  },
  {
    label: "Appointments & Operations", emoji: "📅",
    items: [
      { id: "calendar",  label: "Calendar & Bookings", icon: CalendarDays },
      { id: "tasks",     label: "Tasks",               icon: ListChecks },
      { id: "followups", label: "Follow-Up Center",    icon: Bell },
    ],
  },
  {
    label: "Growth Engine", emoji: "📈",
    items: [
      { id: "seoai",      label: "SEO & AI Search",     icon: Sparkles },
      { id: "aidiscover", label: "AI Discoverability",  icon: Bot },
      { id: "gsc",        label: "Search Performance",  icon: LineChart },
      { id: "seo",        label: "Traffic Analytics",   icon: TrendingUp },
      { id: "contentops", label: "Content Studio",     icon: PenTool },
      { id: "blog",       label: "Blog",                icon: FileText },
    ],
  },
  {
    label: "Authority & Trust", emoji: "🏆",
    items: [
      { id: "testimonials", label: "Testimonials", icon: Quote },
      { id: "cases",        label: "Case Studies", icon: BookOpen },
      { id: "assets",       label: "Assets & Media", icon: ImageIcon },
    ],
  },
  {
    label: "AI & Automation", emoji: "🤖",
    items: [{ id: "aiops", label: "AI Operations", icon: Cpu }],
  },
];

/** Tabs only the owner (super admin) can see. */
export const OWNER_ONLY_TABS: AdminTabId[] = ["seoai", "aidiscover", "gsc", "seo", "aiops"];

interface Props { active: AdminTabId; onSelect: (id: AdminTabId) => void; isOwner?: boolean }

const AdminSidebar = ({ active, onSelect, isOwner = true }: Props) => {
  const groups = GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => isOwner || !OWNER_ONLY_TABS.includes(i.id)) }))
    .filter((g) => g.items.length > 0);
  const { state } = useSidebar();
  const collapsed = state === "collapsed";

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="px-4 py-4 border-b" style={{ borderColor: "hsl(217 50% 22%)" }}>
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center text-sm font-bold"
            style={{ background: "linear-gradient(135deg, hsl(42 55% 55%), hsl(42 65% 45%))", color: "hsl(217 78% 12%)" }}>
            E
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate" style={{ color: "hsl(0 0% 95%)" }}>EGS Command</div>
              <div className="text-[10px] uppercase tracking-wider" style={{ color: "hsl(42 45% 60%)" }}>{isOwner ? "Admin" : "Manager"}</div>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="py-2">
        {groups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel className="text-[10px] font-semibold uppercase tracking-[0.12em]"
              style={{ color: "hsl(42 35% 60%)" }}>
              <span className="mr-1.5">{group.emoji}</span>{!collapsed && group.label}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const isActive = item.id === active;
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        isActive={isActive}
                        tooltip={item.label}
                        onClick={() => onSelect(item.id)}
                        className="data-[active=true]:bg-[hsl(217_60%_22%)] data-[active=true]:text-[hsl(42_55%_70%)] hover:bg-[hsl(217_60%_18%)] hover:text-[hsl(0_0%_95%)]"
                        style={{ color: isActive ? "hsl(42 55% 70%)" : "hsl(217 18% 75%)" }}
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.label}</span>
                        {isActive && (
                          <span className="ml-auto h-1.5 w-1.5 rounded-full"
                            style={{ background: "hsl(42 55% 60%)", boxShadow: "0 0 8px hsl(42 55% 60%)" }} />
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
};

export default AdminSidebar;
